import {
    Compass,
    LineChart,
    ShieldCheck,
    Banknote,
    Handshake,
    BarChart3,
} from "lucide-react";

const SERVICES = [
    {
        icon: Compass,
        title: "Strategic Planning",
        desc: "Long-range plans, scenario modeling, and capital allocation frameworks that align finance with the business roadmap.",
        testid: "service-card-strategy",
    },
    {
        icon: LineChart,
        title: "Financial Modeling & FP&A",
        desc: "Driver-based models, monthly closes, and forward-looking analysis that turn raw numbers into operating leverage.",
        testid: "service-card-modeling",
    },
    {
        icon: Banknote,
        title: "Cash Flow & Treasury",
        desc: "Working capital modeling and optimization.",
        testid: "service-card-cashflow",
    },
    {
        icon: Handshake,
        title: "M&A Activity",
        desc: "End-to-end support across mergers and acquisitions — financial due diligence, valuation, and post-close integration.",
        testid: "service-card-ma",
    },
    {
        icon: ShieldCheck,
        title: "Board & Investor Reporting",
        desc: "Board books, KPI dashboards, and narrative reporting that earn trust and accelerate decisions.",
        testid: "service-card-reporting",
    },
    {
        icon: BarChart3,
        title: "Data Analytics",
        desc: "Advanced analytics including AI modeling, Power BI, Tableau, and custom reporting that turn data into decisions.",
        testid: "service-card-analytics",
    },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-28 md:py-40 px-6 md:px-12 lg:px-20 bg-[#050508] overflow-hidden"
        >
            {/* Soft purple wash */}
            <div
                aria-hidden="true"
                className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(91,58,120,0.18) 0%, transparent 70%)",
                    filter: "blur(50px)",
                }}
            />

            <div className="relative max-w-[1400px] mx-auto">
                <div className="grid grid-cols-12 gap-8 mb-16 md:mb-24">
                    <div className="col-span-12 md:col-span-4">
                        <p className="overline flex items-center gap-3">
                            <span className="lime-dot" />
                            What we do
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <h2
                            data-testid="services-headline"
                            className="font-heading text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                        >
                            CFO-grade finance,{" "}
                            <span className="italic text-[#A3A8D8]">
                                on the terms your stage demands
                            </span>
                            .
                        </h2>
                        <p className="mt-6 max-w-2xl text-white/60 text-base md:text-lg leading-relaxed font-light">
                            Fractional or interim — from early traction to scaled
                            operations, Stout Road plugs in where you need it most,
                            embedded with your team and accountable to outcomes.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[rgba(163,168,216,0.08)]">
                    {SERVICES.map((s) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.title}
                                data-testid={s.testid}
                                className="service-card p-10 md:p-12 flex flex-col"
                            >
                                <div className="icon-wrap w-12 h-12 rounded-sm flex items-center justify-center text-[#A3A8D8] mb-8">
                                    <Icon size={20} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-heading text-2xl md:text-[28px] text-white mb-4 leading-tight">
                                    {s.title}
                                </h3>
                                <p className="text-white/55 text-[15px] leading-relaxed font-light">
                                    {s.desc}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Approach strip */}
                <div id="approach" className="mt-28 md:mt-36 grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-5">
                        <p className="overline flex items-center gap-3">
                            <span className="lime-dot" />
                            Approach
                        </p>
                        <h2 className="font-heading text-white text-3xl md:text-5xl leading-tight mt-6">
                            Clarity drives
                            <br />
                            <span className="italic text-[#A3A8D8]">
                                compounding outcomes
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-7 md:pl-8">
                        <div className="space-y-10">
                            {[
                                {
                                    n: "01",
                                    h: "Diagnose",
                                    p: "We start with a deep listen — your numbers, your team, your pressure points.",
                                },
                                {
                                    n: "02",
                                    h: "Architect",
                                    p: "A finance function tailored to your stage: the right cadence, the right models, the right reporting rhythm.",
                                },
                                {
                                    n: "03",
                                    h: "Operate",
                                    p: "We embed alongside your leadership — quietly indispensable.",
                                },
                            ].map((step) => (
                                <div
                                    key={step.n}
                                    className="flex gap-8 pb-8 border-b border-[rgba(163,168,216,0.1)] last:border-b-0"
                                >
                                    <div className="font-heading text-[#C6F432] text-3xl md:text-4xl w-16 shrink-0">
                                        {step.n}
                                    </div>
                                    <div>
                                        <h4 className="font-heading text-white text-2xl md:text-3xl mb-2">
                                            {step.h}
                                        </h4>
                                        <p className="text-white/55 text-base leading-relaxed font-light max-w-xl">
                                            {step.p}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
